import React, { useEffect, useMemo, useState } from 'react';
import { connect, history, useIntl, useLocation } from 'umi';
import classNames from 'classnames';
import { Card, Layout, Stepper } from 'tea-component';

import UserGuide from '@/pages/datalab/components/UserGuide';
import { CustomPrompt } from '@/pages/datalab/components/CustomPrompt';
import { DATALAB_EDITOR } from '@dbl/core/global';
import { buriedPoint } from '@home/utils/buriedPoint';
import { getYaml } from '@/pages/datalab/pages/Number/ModelManagementCenter/service';
import type { IYamlConfigRetrieveDetail } from '@/pages/datalab/pages/Number/ModelManagementCenter/types';
import StepSelectTables from './components/StepSelectTables';
import StepFieldConfig from './components/StepFieldConfig';
import StepYamlReview from './components/StepYamlReview';
import StepTestPublish from './components/StepTestPublish';
import { WizardStep } from './constant';
import { useModelAgentCreateCache } from './hooks/useModelAgentCreateCache';
import { useModelAgentCreateWizard } from './hooks/useModelAgentCreateWizard';
import type {
  IModelAgentCreateProps,
  IModelAgentCreateRootState,
  IWizardStepperItem,
  TModelAgentCreateLocation,
} from './types';

import './index.scss';

const { Content } = Layout;

const ModelAgentCreate = (props: IModelAgentCreateProps) => {
  const { gameCode, userId, allPrivileges } = props;
  const { formatMessage } = useIntl();
  const location = useLocation() as TModelAgentCreateLocation;
  const editUuid = location?.query?.uuid ?? '';
  const editName = location?.query?.name ?? '';
  /** 与列表「编辑」入口一致：非空 uuid 即编辑（Create 路由不应带 uuid） */
  const isEdit = editUuid?.length > 0;

  /** 编辑 retrieve 全包，供 save 合并 */
  const [editYamlDetail, setEditYamlDetail] = useState<IYamlConfigRetrieveDetail | null>(null);

  const wizard = useModelAgentCreateWizard({ gameCode, editYamlDetail });
  const {
    current,
    setCurrent,
    selectTables,
    changeSelectTables,
    fieldConfig,
    changeFieldConfig,
    yamlDraft,
    changeYamlDraft,
    yamlWorkflow,
    changeYamlWorkflow,
    cardSummary,
    handleStepOneNext,
    handleStepTwoNext,
    handleStepThreeNext,
    handleGenerateYaml,
    applyYamlRetrieveDetail,
  } = wizard;

  // 新建态：未 save 草稿前离开页写 localStorage，再次进入按 gameCode + userId 恢复
  const {
    shouldPromptOnLeave,
    persistWizardCache,
    clearWizardCache,
  } = useModelAgentCreateCache({
    gameCode,
    userId,
    isEdit,
    wizard,
  });

  const handleLeaveNavigate = (
    loc: { pathname: string; search: string; },
  ): void => {
    history.push(loc.pathname + loc.search);
  };

  const steps = useMemo(
    (): IWizardStepperItem[] => [
      {
        id: WizardStep.One,
        label: formatMessage({ id: 'dbl_model_agent_create_step_select_tables' }),
      },
      {
        id: WizardStep.Two,
        label: formatMessage({ id: 'dbl_model_agent_create_step_field_config' }),
      },
      {
        id: WizardStep.Three,
        label: formatMessage({ id: 'dbl_model_agent_create_step_yaml' }),
      },
      {
        id: WizardStep.Four,
        label: formatMessage({ id: 'dbl_model_agent_create_step_test_publish' }),
      },
    ],
    [formatMessage],
  );

  // 新建：需「新建模型」权限 + 602101 打点。
  useEffect(() => {
    if (isEdit) {
      return;
    }
    if (!allPrivileges?.includes(DATALAB_EDITOR.AddModelBtn)) {
      history.replace(
        `/datalab/${gameCode}/DataManagement/ModelManagementCenter`,
      );
      return;
    }
    buriedPoint({
      pageId: '602101',
      pageName: '模型管理中心新建',
      pagePath: '/DataManagement/ModelManagementCenter/Create',
      extInfo: `{"pageName": "Create Data Model", "pageNameCn": "创建新模型"}`,
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
  }, [allPrivileges, gameCode, isEdit]);

  // 离开编辑路由时清空 retrieve 缓存，避免新建页误用旧详情合并 save。
  useEffect(() => {
    if (isEdit) {
      return;
    }
    setEditYamlDetail(null);
  }, [isEdit]);

  // 编辑：602201 打点 + retrieve（与 ModelEdit 同接口），成功后直达 Step3。
  useEffect(() => {
    if (!isEdit) {
      return;
    }
    buriedPoint({
      pageId: '602201',
      pageName: '模型管理中心编辑',
      pagePath: '/DataManagement/ModelManagementCenter/Edit',
      extInfo: JSON.stringify({
        pageName: editName || editUuid,
        pageNameCn: '',
        id: editUuid,
      }),
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
    setCurrent(WizardStep.Three);
    let cancelled = false;
    changeYamlDraft({ yamlLoading: true, yamlError: false });
    getYaml({ uuid: editUuid })
      .then((data) => {
        if (cancelled) {
          return;
        }
        setEditYamlDetail(data);
        applyYamlRetrieveDetail(data, editUuid);
      })
      .catch(() => {
        if (!cancelled) {
          changeYamlDraft({ yamlLoading: false, yamlError: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    applyYamlRetrieveDetail,
    changeYamlDraft,
    editName,
    editUuid,
    gameCode,
    isEdit,
    setCurrent,
  ]);

  return (
    <Content className="dbl-model-agent-create">
      <Content.Header
        showBackButton
        title={
          <UserGuide
            title={formatMessage({
              id: isEdit
                ? 'dbl_model_agent_edit_title'
                : 'dbl_model_agent_create_title',
            })}
          />
        }
        onBackButtonClick={() => {
          // history.goBack 不经过 react-router Prompt，须手动落盘
          if (!isEdit) {
            persistWizardCache();
          }
          history.goBack();
        }}
      />
      {/* 路由离开：主按钮落盘本地进度；次按钮放弃进度（与 CreateReport 按钮语义一致） */}
      {!isEdit && (
        <CustomPrompt
          className="dbl-model-management-leave-prompt"
          caption={formatMessage({
            id: 'dbl_model_management_create_model_leave_title',
          })}
          message={formatMessage({
            id: 'dbl_model_management_create_model_leave_des',
          })}
          isPrompt={shouldPromptOnLeave}
          confirmBtn={formatMessage({
            id: 'dbl_model_agent_create_leave_save_and_leave',
          })}
          cancelBtn={formatMessage({ id: 'dbl_common_leave' })}
          onBeforeunload={() => {
            persistWizardCache();
          }}
          onConfirm={(loc: { pathname: string; search: string; }) => {
            persistWizardCache();
            handleLeaveNavigate(loc);
          }}
          onCancle={(pathname: string) => {
            clearWizardCache();
            history.push(pathname);
          }}
        />
      )}
      <Content.Body full>
        <Card full>
          <div className="dbl-model-agent-create-body">
            <Stepper steps={steps} current={current} />
            <div
              className={classNames(
                `dbl-model-agent-create-body-content dbl-model-agent-create-body-content-${current}`,
              )}
            >
              {current === WizardStep.One && (
                <StepSelectTables
                  gameCode={gameCode}
                  setCurrent={setCurrent}
                  value={selectTables}
                  onChange={changeSelectTables}
                  handleNext={handleStepOneNext}
                  prepareFieldConfigLoading={fieldConfig.fieldLoading}
                />
              )}
              {current === WizardStep.Two && (
                <StepFieldConfig
                  gameCode={gameCode}
                  setCurrent={setCurrent}
                  value={fieldConfig}
                  onChange={changeFieldConfig}
                  handleNext={handleStepTwoNext}
                  generateYamlLoading={yamlDraft.yamlLoading}
                />
              )}
              {current === WizardStep.Three && (
                <StepYamlReview
                  gameCode={gameCode}
                  setCurrent={setCurrent}
                  yamlDraft={yamlDraft}
                  onYamlDraftChange={changeYamlDraft}
                  onRegenerateYaml={handleGenerateYaml}
                  cardSummary={cardSummary}
                  saveDraftLoading={yamlWorkflow.saveDraftLoading}
                  handleNext={handleStepThreeNext}
                />
              )}
              {current === WizardStep.Four && (
                <StepTestPublish
                  gameCode={gameCode}
                  setCurrent={setCurrent}
                  allPrivileges={allPrivileges}
                  selectTables={selectTables}
                  fieldConfig={fieldConfig}
                  yamlDraft={yamlDraft}
                  cardSummary={cardSummary}
                  onYamlDraftChange={changeYamlDraft}
                  yamlWorkflow={yamlWorkflow}
                  onYamlWorkflowChange={changeYamlWorkflow}
                />
              )}
            </div>
          </div>
        </Card>
      </Content.Body>
    </Content>
  );
};

export default connect((state: IModelAgentCreateRootState) => ({
  allPrivileges: state.privilegesConfig.allPrivileges,
  gameCode: state.datalab.gameCode,
  userId: state?.home?.userInfo?.userid ?? '',
}))(ModelAgentCreate);
